'use client'

import * as React from 'react'
import { Calendar, ChevronRight } from 'lucide-react'

export interface CrpoHistoryResult {
  id: string
  path: string
  clientHours: number | null
  clientHoursUnknown: boolean
  supervisionIndividual: number | null
  supervisionIndividualUnknown: boolean
  supervisionGroup: number | null
  supervisionGroupUnknown: boolean
  targetExam: string | null
  createdAt: string
}

export interface CrpoHistoryListProps {
  results: CrpoHistoryResult[]
  onResultClick: (id: string) => void
  emptyStateAction?: () => void
  emptyStateActionLabel?: string
}

const REQUIREMENTS = {
  exam: { clientHours: 450, supervisionTotal: 100, supervisionIndividual: 50, supervisionGroup: 50 },
  independent: { clientHours: 1000, supervisionTotal: 150, supervisionIndividual: 75, supervisionGroup: 75 },
}

function calculateProgress(result: CrpoHistoryResult) {
  const req = REQUIREMENTS[result.path as keyof typeof REQUIREMENTS] || REQUIREMENTS.exam
  const clientHours = result.clientHoursUnknown ? 0 : (result.clientHours || 0)
  const indHours = result.supervisionIndividualUnknown ? 0 : (result.supervisionIndividual || 0)
  const grpHours = result.supervisionGroupUnknown ? 0 : (result.supervisionGroup || 0)

  const groupCounted = Math.min(grpHours, req.supervisionGroup)
  const effectiveTotal = indHours + groupCounted

  const clientProgress = Math.min(100, Math.round((clientHours / req.clientHours) * 100))
  const supProgress = Math.min(100, Math.round((effectiveTotal / req.supervisionTotal) * 100))
  const overallProgress = Math.round((clientProgress + supProgress) / 2)

  return { clientProgress, supProgress, overallProgress, clientHours, effectiveTotal, req }
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
   timeZone: 'America/Toronto'})
}

// Only spring/fall 2026+ sittings we currently show in the wizard's dropdown.
// Fallback to the raw stored value if we don't have a friendly label.
const SITTING_LABELS: Record<string, string> = {
  '2026-04-28': 'Spring 2026',
  '2026-11-15': 'Fall 2026',
  '2027-04-15': 'Spring 2027',
  '2027-11-15': 'Fall 2027',
  '2028-04-15': 'Spring 2028',
}

export function CrpoHistoryList({
  results,
  onResultClick,
  emptyStateAction,
  emptyStateActionLabel = 'Start Calculator',
}: CrpoHistoryListProps) {
  if (results.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-12 shadow-[0_2px_20px_rgba(28,26,43,0.06)] text-center border border-[#E4E1F3]">
        <div className="w-16 h-16 bg-[#EDEAFC] rounded-2xl flex items-center justify-center text-3xl mx-auto mb-5">
          📊
        </div>
        <h2 className="font-semibold text-[#1C1A2B] mb-2">No results yet</h2>
        <p className="text-[#5A5872] mb-6">
          Complete the CRPO Progress Calculator to see your results here.
        </p>
        {emptyStateAction && (
          <button
            onClick={emptyStateAction}
            className="inline-flex px-6 py-3 rounded-full font-bold bg-gradient-to-r from-[#6C5CE7] to-[#4C3FB8] hover:shadow-[0_8px_28px_rgba(108,92,231,0.35)] text-white transition-all"
          >
            {emptyStateActionLabel} →
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {results.map((result) => {
        const { clientProgress, supProgress, overallProgress, clientHours, effectiveTotal, req } = calculateProgress(result)

        return (
          <button
            key={result.id}
            onClick={() => onResultClick(result.id)}
            className="block w-full text-left bg-white rounded-2xl p-6 shadow-[0_2px_20px_rgba(28,26,43,0.06)] hover:shadow-[0_4px_30px_rgba(28,26,43,0.1)] hover:border-[#C4BAE8] transition-all group border border-[#E4E1F3]"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-[#EDEAFC] text-[#4C3FB8] mb-2">
                  {result.path === 'exam' ? '🎯 Exam Path' : '🏠 Independent Practice'}
                </div>
                <div className="flex items-center gap-2 text-sm text-[#5A5872]">
                  <Calendar className="h-4 w-4" />
                  {formatDate(result.createdAt)}
                </div>
              </div>
              <div className="text-right flex items-center gap-3">
                <div>
                  <div className="text-3xl font-bold text-[#1C1A2B]">{overallProgress}%</div>
                  <div className="text-xs text-[#5A5872]">overall progress</div>
                </div>
                <ChevronRight className="h-5 w-5 text-[#8886A0] group-hover:text-[#4C3FB8] group-hover:translate-x-1 transition-all" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[#FAFAFE] rounded-xl p-4 border border-[#E4E1F3]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-[#4C3FB8] uppercase tracking-wide">Client Hours</span>
                  <span className="text-xs text-[#5A5872]">{clientProgress}%</span>
                </div>
                <div className="h-2 bg-[#E4E1F3] rounded-full overflow-hidden mb-2">
                  <div
                    className="h-full bg-gradient-to-r from-[#6C5CE7] to-[#8B7EF0] rounded-full"
                    style={{ width: `${clientProgress}%` }}
                  />
                </div>
                <div className="text-sm font-semibold text-[#1C1A2B]">
                  {result.clientHoursUnknown ? '—' : clientHours} / {req.clientHours}
                </div>
              </div>

              <div className="bg-[#FAFAFE] rounded-xl p-4 border border-[#E4E1F3]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-[#4C3FB8] uppercase tracking-wide">Supervision</span>
                  <span className="text-xs text-[#5A5872]">{supProgress}%</span>
                </div>
                <div className="h-2 bg-[#E4E1F3] rounded-full overflow-hidden mb-2">
                  <div
                    className="h-full bg-gradient-to-r from-[#4C3FB8] to-[#6C5CE7] rounded-full"
                    style={{ width: `${supProgress}%` }}
                  />
                </div>
                <div className="text-sm font-semibold text-[#1C1A2B]">
                  {(result.supervisionIndividualUnknown && result.supervisionGroupUnknown) ? '—' : effectiveTotal} / {req.supervisionTotal}
                </div>
              </div>
            </div>

            {result.targetExam && result.targetExam !== 'later' && (
              <div className="mt-4 pt-4 border-t border-[#E4E1F3]">
                <div className="flex items-center gap-2 text-sm text-[#5A5872]">
                  <span>🎯 Target:</span>
                  <span className="font-medium text-[#1C1A2B]">
                    {SITTING_LABELS[result.targetExam] ?? result.targetExam}
                  </span>
                </div>
              </div>
            )}
          </button>
        )
      })}
    </div>
  )
}
